import { useCallback } from 'react';
import { useFormikContext } from 'formik';
import type { ChangeEvent } from 'react';
import AlertTitle from '@mui/material/AlertTitle';
import Grid from '@mui/material/Grid';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';

import { ensureDefined } from '@e-socna/utils';
import { EMPTY_ARRAY, EMPTY_STRING } from '@e-socna/empty';
import {
  getFragmentData,
  graphql,
  MissionKindEnum,
  SubMissionActorCaseEnum,
} from '@e-socna/data-layer';
import type { Id } from '@e-socna/data-layer';

import { useGraphQLQuery } from '../../../hooks/useGraphQLQuery.hook';
import { useReferencesContext } from '../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { hasSubMissions } from '../../../app/utils/mission-kind-profile.util';
import { Access_Answer_Fragment } from '../../../components/Access/fragments/access-answer.fragment';
import { gateOf } from '../../../components/Access/utils/access-gate.util';

import { useSubMissionQuotationCase } from '../hooks/useSubMissionQuotationCase.hook';
import { useMissionWorkflowFamily } from '../hooks/useMissionWorkflowFamily.hook';

import {
  canBeTurnedIntoCollaboration,
  isTurningIntoCollaboration,
  NEW_COLLABORATION_ID,
} from '../utils/collaboration-turn.util';
import { hasCollaborationWorkflows } from '../utils/mission-workflow-family.util';
import { filterMissionWorkflowsOfKind } from '../utils/mission-workflows-of-kind.util';
import type { MissionForm_Mission } from '../fragments/mission.fragment';

import { useQuotationCollaboration } from '../hooks/useQuotationCollaboration.hook';
import { InfoAlert } from '../../../components/InfoAlert';

export const MissionForm_CollaborationStep_Composites_QueryDocument = graphql(`
  query MissionForm_CollaborationStep_Composites_Query($folderId: Id!) {
    findMissionsWithinAFolder(folderId: $folderId) {
      _id
      kind
      code
      consolidation {
        title
      }
      access {
        updateQuotationRelatedFields: can(
          scope: "updateQuotationRelatedFields"
        ) {
          ...Access_Answer_Fragment
        }
      }
    }
  }
`);

const NO_COLLABORATION = EMPTY_STRING;

const OPTION_SX = { my: 0 } as const;

interface Props {
  readonly id: string;
}

// eslint-disable-next-line no-empty-pattern
export const MissionCollaborationFormStep = ({}: Readonly<Props>) => {
  const { initialValues, setValues, values } =
    useFormikContext<MissionForm_Mission>();
  const { familyMissionWorkflows } = useMissionWorkflowFamily(
    initialValues.workflowId,
  );
  const canJoinACollaboration = hasCollaborationWorkflows(
    familyMissionWorkflows,
  );
  const canTurnIntoCollaboration =
    canJoinACollaboration && canBeTurnedIntoCollaboration(initialValues);
  const isTurning = isTurningIntoCollaboration(values);

  const referencesContext = useReferencesContext();
  const { defaultMissionWorkflow } = ensureDefined({
    defaultMissionWorkflow: referencesContext.defaultMissionWorkflow,
  });
  const standardWorkflowId =
    filterMissionWorkflowsOfKind(
      familyMissionWorkflows,
      MissionKindEnum.Standard,
    )[0]?._id ?? defaultMissionWorkflow._id;
  const { workflowIdOfCase } = useSubMissionQuotationCase(values.workflowId);
  const isSubMission = !!initialValues.compositeMissionId;
  const { formLists: formerCollaborationLists } = useQuotationCollaboration(
    initialValues.compositeMissionId,
  );

  const { data } = useGraphQLQuery(
    MissionForm_CollaborationStep_Composites_QueryDocument,
    { folderId: values.folderId },
    { enabled: !!values.folderId },
  );

  const composites = canJoinACollaboration
    ? (data?.findMissionsWithinAFolder ?? EMPTY_ARRAY).filter(
        (mission) => hasSubMissions(mission.kind) && mission._id !== values._id,
      )
    : EMPTY_ARRAY;

  // The actors follow the collaboration, their configuration being edited in
  // the actor tabs: those of the chosen one (devis externe), those of the
  // folder once detached, the former ones back in the collaboration of the
  // mission. One update, so that they are validated with the workflow.
  const handleCompositeChange = useCallback(
    (event: Readonly<ChangeEvent<HTMLInputElement>>) => {
      const compositeMissionId = event.target.value as Id;

      // The new collaboration takes the actors of the mission, those of its
      // former collaboration for a sub-mission.
      if (compositeMissionId === NEW_COLLABORATION_ID) {
        void setValues({
          ...values,
          kind: MissionKindEnum.SubMission,
          compositeMissionId,
          ...(isSubMission
            ? formerCollaborationLists
            : {
                internalActors: initialValues.internalActors,
                externalActors: initialValues.externalActors,
              }),
          workflowId:
            workflowIdOfCase(SubMissionActorCaseEnum.SameEmitterAndReceiver) ??
            values.workflowId,
        });
        return;
      }

      if (compositeMissionId === NO_COLLABORATION) {
        void setValues({
          ...values,
          kind: MissionKindEnum.Standard,
          compositeMissionId: null,
          workflowId: standardWorkflowId,
          internalActors: null,
          externalActors: null,
        });
        return;
      }

      const isInitialCollaboration =
        compositeMissionId === initialValues.compositeMissionId;

      void setValues({
        ...values,
        kind: MissionKindEnum.SubMission,
        compositeMissionId,
        workflowId: isInitialCollaboration
          ? initialValues.workflowId
          : (workflowIdOfCase(SubMissionActorCaseEnum.SameEmitterAndReceiver) ??
            values.workflowId),
        internalActors: isInitialCollaboration
          ? initialValues.internalActors
          : null,
        externalActors: isInitialCollaboration
          ? initialValues.externalActors
          : null,
      });
    },
    [
      formerCollaborationLists,
      isSubMission,
      initialValues.internalActors,
      initialValues.externalActors,
      initialValues.compositeMissionId,
      initialValues.workflowId,
      setValues,
      standardWorkflowId,
      values,
      workflowIdOfCase,
    ],
  );

  if (!data) {
    return (
      <Grid container spacing={2} sx={{ alignContent: 'flex-start', pt: 2 }}>
        <Grid size={12}>
          <TextField
            disabled
            fullWidth
            id="mission-collaboration-composite-mission-id"
            label="Collaboration"
            value="Chargement des Collaborations du dossier..."
          />
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container spacing={2} sx={{ alignContent: 'flex-start', pt: 2 }}>
      <Grid size={12}>
        <TextField
          fullWidth
          id="mission-collaboration-composite-mission-id"
          label="Collaboration"
          onChange={handleCompositeChange}
          select
          value={values.compositeMissionId ?? NO_COLLABORATION}
        >
          <MenuItem value={NO_COLLABORATION}>
            <ListItemText primary="Aucune (mission simple)" sx={OPTION_SX} />
          </MenuItem>
          {canTurnIntoCollaboration ? (
            <MenuItem value={NEW_COLLABORATION_ID}>
              <ListItemText
                primary={
                  isSubMission
                    ? 'Nouvelle Collaboration (transformer la sous-mission)'
                    : 'Nouvelle Collaboration (transformer la mission simple)'
                }
                sx={OPTION_SX}
              />
            </MenuItem>
          ) : null}
          {composites.map((composite) => {
            // A collaboration whose submitted quotation freezes its sub-missions takes no other.
            const gate = gateOf(
              getFragmentData(
                Access_Answer_Fragment,
                composite.access.updateQuotationRelatedFields,
              ),
            );
            const isLocked =
              !gate.isAllowed &&
              composite._id !== initialValues.compositeMissionId;

            return (
              <MenuItem
                disabled={isLocked}
                key={composite._id}
                value={composite._id}
              >
                <ListItemText
                  primary={composite.consolidation.title}
                  secondary={
                    isLocked
                      ? `${composite.code} — ${gate.reasons.join(' ')}`
                      : composite.code
                  }
                  sx={OPTION_SX}
                />
              </MenuItem>
            );
          })}
        </TextField>
      </Grid>
      {isTurning ? (
        <Grid size={12}>
          {isSubMission ? (
            <InfoAlert>
              <AlertTitle>La sous-mission quitte sa Collaboration</AlertTitle>
              Une Collaboration est créée dans le dossier avec son titre et les
              acteurs de sa Collaboration actuelle, dont elle devient la
              première sous-mission, en devis externe.
              <br />
              Elle conserve son type, ses rapports et son chiffrage.
            </InfoAlert>
          ) : (
            <InfoAlert>
              Une Collaboration est créée dans le dossier avec le titre et les
              acteurs de la mission, qui en devient la première sous-mission, en
              devis externe.
              <br />
              La mission conserve son type, ses rapports et son chiffrage.
            </InfoAlert>
          )}
        </Grid>
      ) : null}
      {values.compositeMissionId &&
      !isTurning &&
      values.compositeMissionId !== initialValues.compositeMissionId ? (
        <Grid size={12}>
          <InfoAlert>
            La sous-mission passe en devis externe avec les acteurs de cette
            Collaboration
            {initialValues.internalActors || initialValues.externalActors
              ? ' (ses acteurs propres sont remplacés)'
              : ''}
            . Sa configuration (devis interne, émetteur du devis) se modifie
            ensuite dans ses onglets Acteurs.
          </InfoAlert>
        </Grid>
      ) : null}
    </Grid>
  );
};
